import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../api/index';
import { Button } from '../components/Button';
import { getPasswordStrengthMessage, validatePassword } from '../lib/validation';
export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      toast.error('Invalid or missing reset token')
      navigate('/login')
    }
  }, [token, navigate])

  const resetPasswordMutation = useMutation({
    mutationFn: () => authAPI.resetPassword(token!, newPassword),
    onSuccess: () => {
      toast.success('Password reset successfully!')
      setIsSuccess(true)
      setTimeout(() => navigate('/login'), 2000)
    },
    onError: (error: any) => {
      console.error('Reset password error:', error)
      const errorMessage = error.response?.data?.error || 'Failed to reset password'
      if (errorMessage.includes('expired')) {
        toast.error('This reset link has expired. Please request a new one.')
        setTimeout(() => navigate('/forgot-password'), 2000)
      } else if (errorMessage.includes('already used')) {
        toast.error('This reset link has already been used. Please request a new one.')
        setTimeout(() => navigate('/forgot-password'), 2000)
      } else {
        toast.error(errorMessage)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields')
      return
    }

    if (!validatePassword(newPassword)) {
      const requirements = getPasswordStrengthMessage()
      toast.error(`Password must contain: ${requirements.join(', ')}`)
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    resetPasswordMutation.mutate()
  }

  if (!token) {
    return null
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy text-white flex-col justify-center items-center p-12">
        <div className="max-w-md">
          <div className="text-5xl font-bold mb-6">RTO Agents</div>
          <p className="text-xl text-gray-300 mb-8">
            Manage your agency's vehicle documents with ease
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Complete document management system</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Real-time expiry tracking and alerts</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Team collaboration and permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - Reset Password form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-white">
        <div className="max-w-md w-full">
          {!isSuccess ? (
            <>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Reset Your Password</h1>
                <p className="text-gray-600 mt-2">
                  Create a strong, unique password for your account.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={resetPasswordMutation.isPending}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Must contain: 8+ chars, 1 uppercase, 1 lowercase, 1 digit, 1 special char (!@#$%^&*)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={resetPasswordMutation.isPending}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  isLoading={resetPasswordMutation.isPending}
                  disabled={resetPasswordMutation.isPending}
                >
                  Reset Password
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Password Reset Successful!
                </h2>
                <p className="text-gray-600">
                  Your password has been reset successfully. Redirecting to sign in...
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
